import React, { useState, useEffect } from 'react';
// [Web UX 3. csomag (D), 2026-10-04] A kérés-hibák szövege a közös `apiErrorMessage`-en át megy (a szerver magyar
// indoklása / hálózati hibánál magyar mondat / különben „<tartalék> (HTTP n)") — a korábbi FIX
// szövegű ágak eldobták a szerver indoklását. Őr: `utils/__tests__/rawErrorMessages.guard.test.ts`.
import { apiErrorMessage } from '../../utils/apiErrors';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
} from '@mui/icons-material';
import { calendarService } from '../../services/calendarService';
import { CalendarEvent } from '../../types/calendar';
import { getTodayLocal } from '../../utils/dateUtils';
import EventDetailsDialog from './EventDetailsDialog';
import RegisteredMarker, { showRegisteredMarker } from './RegisteredMarker';
import EventEditDialog from './EventEditDialog';
import EventCreateDialog from './EventCreateDialog';
import { useAuth } from '../../contexts/AuthContext';
import { NAV_BUTTON_TOUCH_SX, buttonKeyHandlers, cardFocusRingSx, eventCardAriaLabel } from './calendarA11y';

interface WeeklyCalendarProps {
  selectedDate: string;
  onDateChange: (date: string) => void;
  onDateSelect?: (date: string) => void;
  venueFilter?: string;
  availableOnly?: boolean;
  /** Elrejti a betelt eseményeket, amelyeknek a felhasználó nem résztvevője. */
  excludeFullOthers?: boolean;
  eventTypeFilter?: string[];
  coachFilter?: string[];
  refreshTrigger?: number;
  onNavigateToUserProfile?: (userId: string) => void;
  /** Az esemény ablakából a jelenléti ívre visz, ARRA az eseményre. */
  onNavigateToAttendance?: (eventId: string, eventDate: string) => void;
}

/**
 * A heti kártya kapacitás-címkéje — a látható chip ÉS a kártya akadálymentes neve is ebből jön
 * ([Web UX 9 / T5, 2026-10-13]), hogy a kettő ne térhessen el.
 * A KORLÁTLAN esemény nem „Megtelt". A szabad helyek számítása létszámkorlát nélkül 0-t ad, ezért
 * korlátlan eseményre is „Megtelt" került — például az asztalfoglalásokra. A szűrő ezeket helyesen
 * megtartja, így a felület és a szűrő ellentmondott egymásnak.
 */
const weeklyCapacityLabel = (event: CalendarEvent): string =>
  event.maxParticipants == null
    ? 'Nincs korlát'
    : event.availableSpots > 0 ? 'Van hely' : 'Megtelt';

interface DayColumn {
  date: string;
  dayName: string;
  dayNumber: number;
  isToday: boolean;
  events: CalendarEvent[];
}

const WeeklyCalendar: React.FC<WeeklyCalendarProps> = ({
  selectedDate,
  onDateChange,
  onDateSelect,
  venueFilter,
  availableOnly,
  excludeFullOthers,
  eventTypeFilter,
  coachFilter,
  refreshTrigger,
  onNavigateToUserProfile,
  onNavigateToAttendance,
}) => {
  const { user } = useAuth();
  const [weekDays, setWeekDays] = useState<DayColumn[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [weekStart, setWeekStart] = useState<string>('');

  // Dialog states
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [createEventDate, setCreateEventDate] = useState<string>('');
  const [createEventTime, setCreateEventTime] = useState<string>('');

  const isAdminOrCoach = user?.userRole === 'Administrator' || user?.userRole === 'Coach';

  // Get the start of the week (Monday) for a given date
  const getWeekStart = (dateStr: string): string => {
    // Parse date parts to avoid timezone issues
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    const dayOfWeek = date.getDay(); // 0=Sunday, 1=Monday, 2=Tuesday, ..., 6=Saturday

    // Calculate days to subtract to get to Monday
    // If Sunday (0), go back 6 days; if Monday (1), go back 0 days; if Tuesday (2), go back 1 day, etc.
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    // Create Monday date
    const monday = new Date(year, month - 1, day - daysToMonday);

    // Format as YYYY-MM-DD using local date
    const mondayYear = monday.getFullYear();
    const mondayMonth = String(monday.getMonth() + 1).padStart(2, '0');
    const mondayDay = String(monday.getDate()).padStart(2, '0');
    const result = `${mondayYear}-${mondayMonth}-${mondayDay}`;

    console.log('🔧 getWeekStart:', dateStr, '(day:', dayOfWeek, ', daysToMonday:', daysToMonday, ') → Monday:', result);
    return result;
  };

  // Calculate week days from week start
  const calculateWeekDays = (startDate: string): DayColumn[] => {
    const days: DayColumn[] = [];
    const today = getTodayLocal();
    const dayNames = ['Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek', 'Szombat', 'Vasárnap'];

    // Parse startDate using local timezone
    const [year, month, day] = startDate.split('-').map(Number);

    for (let i = 0; i < 7; i++) {
      // Create date in local timezone
      const date = new Date(year, month - 1, day + i);

      // Format as YYYY-MM-DD
      const dateYear = date.getFullYear();
      const dateMonth = String(date.getMonth() + 1).padStart(2, '0');
      const dateDay = String(date.getDate()).padStart(2, '0');
      const dateStr = `${dateYear}-${dateMonth}-${dateDay}`;

      days.push({
        date: dateStr,
        dayName: dayNames[i],
        dayNumber: date.getDate(),
        isToday: dateStr === today,
        events: [],
      });
    }

    return days;
  };

  // Load events for the week
  const loadWeekEvents = async (startDate: string) => {
    setLoading(true);
    setError(null);

    try {
      const endDate = new Date(startDate + 'T00:00:00');
      endDate.setDate(endDate.getDate() + 6);
      const endYear = endDate.getFullYear();
      const endMonth = String(endDate.getMonth() + 1).padStart(2, '0');
      const endDay = String(endDate.getDate()).padStart(2, '0');
      const endDateStr = `${endYear}-${endMonth}-${endDay}`;

      const rawEvents = await calendarService.getCalendarEvents({
        startDate,
        endDate: endDateStr,
        venueId: venueFilter,
        availableOnly,
        excludeFullOthers,
        // Az esemény-típus szűrő TÖBB-választós, a `GET /api/calendar/events` viszont csak EGY
        // `eventTypeId`-t vesz (`CalendarController.cs:38-40`). Eddig itt `eventTypeFilter?.[0]`
        // állt, tehát két kijelölt típusból CSAK AZ ELSŐRE szűrt a heti nézet — a másik típus
        // eseményei eltűntek. Ezért a típus-szűrés (a NAPI nézet mintájára) kliens-oldali.
        coachIds: coachFilter && coachFilter.length > 0 ? coachFilter : undefined,
      });

      const events = eventTypeFilter && eventTypeFilter.length > 0
        ? rawEvents.filter((event: CalendarEvent) =>
            eventTypeFilter.some(typeId => event.eventTypeId === typeId))
        : rawEvents;

      // Group events by date
      const days = calculateWeekDays(startDate);
      events.forEach((event: CalendarEvent) => {
        const day = days.find((d) => d.date === event.eventDate);
        if (day) {
          day.events.push(event);
        }
      });

      // Sort events by start time within each day
      days.forEach((day) => {
        day.events.sort((a, b) => a.startTime.localeCompare(b.startTime));
      });

      setWeekDays(days);
    } catch (err) {
      console.error('Error loading week events:', err);
      setError(apiErrorMessage(err, 'Nem sikerült betölteni az eseményeket'));
    } finally {
      setLoading(false);
    }
  };

  // Initialize week start and load events when weekStart changes
  useEffect(() => {
    if (!weekStart) {
      // Initial load - set weekStart from selectedDate
      const start = getWeekStart(selectedDate);
      console.log('⚙️ Initial load - setting weekStart to:', start);
      setWeekStart(start);
      return;
    }

    // Load events whenever weekStart changes
    console.log('📢 weekStart changed to:', weekStart, '- loading events');
    loadWeekEvents(weekStart);
  }, [weekStart]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload events when filters change
  useEffect(() => {
    if (weekStart && (venueFilter !== undefined || availableOnly !== undefined || excludeFullOthers !== undefined ||
        eventTypeFilter !== undefined || coachFilter !== undefined ||
        refreshTrigger !== undefined)) {
      console.log('🔄 Filters changed - reloading events for current week:', weekStart);
      loadWeekEvents(weekStart);
    }
    // ⚠️ Az `excludeFullOthers` NEM hagyható ki innen. A feltétel fent hivatkozik rá,
    // de ha a függőségek közt nincs ott, a hatás a pipa átbillentésekor NEM fut le újra:
    // a kezdeti betöltés még helyes, a KIKAPCSOLÁS viszont nem csinál semmit. És a heti
    // az alapértelmezett nézet, tehát ez a leggyakoribb út.
  }, [venueFilter, availableOnly, excludeFullOthers, eventTypeFilter, coachFilter, refreshTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  const navigateWeek = (direction: 'prev' | 'next') => {
    console.log('🔵 navigateWeek clicked:', direction, 'current weekStart:', weekStart);

    // Parse current weekStart using local timezone (avoid UTC issues)
    const [year, month, day] = weekStart.split('-').map(Number);
    const currentStart = new Date(year, month - 1, day);

    // Add or subtract 7 days
    const daysToAdd = direction === 'next' ? 7 : -7;
    const newDate = new Date(year, month - 1, day + daysToAdd);

    // Format as YYYY-MM-DD
    const newYear = newDate.getFullYear();
    const newMonth = String(newDate.getMonth() + 1).padStart(2, '0');
    const newDay = String(newDate.getDate()).padStart(2, '0');
    const newStartStr = `${newYear}-${newMonth}-${newDay}`;

    // Ensure we always start on Monday
    const newWeekStart = getWeekStart(newStartStr);

    console.log('🟢 navigateWeek changing from:', weekStart, 'to:', newWeekStart, '(calculated from:', newStartStr, ')');
    setWeekStart(newWeekStart);
    onDateChange(newWeekStart);
  };

  const goToToday = () => {
    const today = getTodayLocal();
    const start = getWeekStart(today);
    setWeekStart(start);
    onDateChange(today);
  };

  const handleEventClick = (eventId: string) => {
    setSelectedEventId(eventId);
    setDetailsDialogOpen(true);
  };

  const handleEditClick = (eventId: string) => {
    setSelectedEventId(eventId);
    setEditDialogOpen(true);
  };

  const handleDayClick = (date: string, time: string = '09:00') => {
    if (isAdminOrCoach) {
      setCreateEventDate(date);
      setCreateEventTime(time);
      setCreateDialogOpen(true);
    } else if (onDateSelect) {
      onDateSelect(date);
    }
  };

  const handleDayHeaderClick = (date: string) => {
    if (onDateSelect) {
      onDateSelect(date);
    }
  };

  const handleDialogClose = () => {
    setDetailsDialogOpen(false);
    setEditDialogOpen(false);
    setCreateDialogOpen(false);
    setSelectedEventId(null);
    // Refresh events after any dialog closes
    loadWeekEvents(weekStart);
  };

  const formatWeekRange = (): string => {
    if (weekDays.length === 0) return '';

    // Parse dates using local timezone (avoid UTC issues)
    const parseLocalDate = (dateStr: string) => {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    };

    const start = parseLocalDate(weekDays[0].date);
    const end = parseLocalDate(weekDays[6].date);
    const monthNames = [
      'Január', 'Február', 'Március', 'Április', 'Május', 'Június',
      'Július', 'Augusztus', 'Szeptember', 'Október', 'November', 'December'
    ];

    if (start.getMonth() === end.getMonth()) {
      return `${monthNames[start.getMonth()]} ${start.getDate()}-${end.getDate()}, ${start.getFullYear()}`;
    } else {
      return `${monthNames[start.getMonth()]} ${start.getDate()} - ${monthNames[end.getMonth()]} ${end.getDate()}, ${start.getFullYear()}`;
    }
  };

  if (loading && weekDays.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Week Navigation Header */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {formatWeekRange()}
          </Typography>

          {/* [Web UX 9 / T5, 2026-10-13] A lapozó-gombok címke nélküliek voltak (képernyőolvasó: „gomb"),
              telefonon ~34 px-esek (`WeeklyCalendar.tsx:337-345`, UX-lista 6.1). A „Mai nap" felirat a havi és
              napi nézet súgójával egyezik (`MonthlyCalendar.tsx`, `DailyTimeline.tsx`). Őr:
              `utils/__tests__/iconButtonLabels.guard.test.ts`. */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={() => navigateWeek('prev')} size="small" aria-label="Előző hét" sx={NAV_BUTTON_TOUCH_SX}>
              <ChevronLeftIcon />
            </IconButton>
            <IconButton onClick={goToToday} size="small" color="primary" aria-label="Mai nap" sx={NAV_BUTTON_TOUCH_SX}>
              <TodayIcon />
            </IconButton>
            <IconButton onClick={() => navigateWeek('next')} size="small" aria-label="Következő hét" sx={NAV_BUTTON_TOUCH_SX}>
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Week Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(7, 1fr)',
          },
          gap: 0.25,
        }}
      >
        {weekDays.map((day) => (
          <Paper
            key={day.date}
            sx={{
              p: 0.5,
              minHeight: 200,
              border: day.isToday ? '2px solid' : '1px solid',
              borderColor: day.isToday ? 'primary.main' : 'divider',
              backgroundColor: day.isToday ? 'action.hover' : 'background.paper',
              cursor: isAdminOrCoach ? 'pointer' : 'default',
              '&:hover': isAdminOrCoach
                ? {
                    backgroundColor: 'action.selected',
                  }
                : {},
            }}
            onClick={() => handleDayClick(day.date)}
          >
            {/* Day Header */}
            <Box
              sx={{ mb: 0.5, cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                handleDayHeaderClick(day.date);
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: day.isToday ? 700 : 500,
                  color: day.isToday ? 'primary.main' : 'text.secondary',
                  fontSize: { xs: '0.65rem', sm: '0.7rem' },
                }}
              >
                {day.dayName}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: day.isToday ? 700 : 400,
                  color: day.isToday ? 'primary.main' : 'text.primary',
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {day.dayNumber}
              </Typography>
            </Box>

            {/* Events List */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              {day.events.length === 0 ? (
                <Typography
                  variant="body2"
                  color="warning.main"
                  sx={{
                    textAlign: 'center',
                    py: 1,
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    fontWeight: 600
                  }}
                >
                  Nincs esemény
                </Typography>
              ) : (
                day.events.map((event) => {
                  const showCapacity = event.availableSpots !== undefined && event.eventTypeDisplayName !== 'Asztalfoglalás';
                  return (
                  <Tooltip
                    key={event.eventId}
                    title={`${event.eventName} - ${event.venueName || 'Nincs helyszín'}`}
                    arrow
                  >
                    {/* [Web UX 9 / T5, 2026-10-13] A kártya billentyűzetről elérhetetlen volt (`onClick`-es Box,
                        `WeeklyCalendar.tsx:440`, UX-lista 6.2): most `role="button"` + `tabIndex` + Enter/Szóköz
                        (`buttonKeyHandlers`), látható fókusz-gyűrűvel. Kinézet nem változik (nem ButtonBase,
                        hogy a `display`/`text-align` ne módosuljon). Az akadálymentes név a kártyán látható
                        adatokat mondja ki, „Jelentkeztél: " előtaggal, mint a havi chip; a Tooltip saját
                        `aria-label`-jét a gyermek felülírja (MUI `Tooltip.js`: `...children.props` a végén). */}
                    <Box
                      role="button"
                      tabIndex={0}
                      aria-label={eventCardAriaLabel({
                        eventName: event.eventName,
                        startTime: event.startTime,
                        endTime: event.endTime,
                        venueName: event.venueName,
                        extra: [showCapacity && weeklyCapacityLabel(event)],
                        registered: showRegisteredMarker(event),
                      })}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEventClick(event.eventId);
                      }}
                      {...buttonKeyHandlers(() => handleEventClick(event.eventId))}
                      sx={{
                        p: 0.5,
                        borderRadius: 1,
                        backgroundColor: event.eventTypeColor || 'primary.main',
                        color: '#fff',
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 0.8,
                        },
                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        // a fókuszált kártya gyűrűje a szomszéd kártya fölé kerüljön
                        position: 'relative',
                        '&:focus-visible': { ...cardFocusRingSx['&:focus-visible'], zIndex: 1 },
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          display: 'block',
                          fontSize: 'inherit',
                          color: 'inherit',
                        }}
                      >
                        {event.startTime.substring(0, 5)} - {event.endTime.substring(0, 5)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: 'inherit',
                          fontWeight: 600,
                          color: 'inherit',
                        }}
                      >
                        {event.eventName}
                      </Typography>
                      {/*
                        [Web UX 1. csomag, 2026-10-03] „Jelentkeztél" a kapacitás-címke MELLETT
                        (mindkettő látszik, egy sorban, szükség esetén tördelve).
                        Validátor-lelet (2026-10-03): a korábbi változat a jelzőt a „Van hely /
                        Megtelt / Nincs korlát" címke HELYÉRE tette azzal az indokkal, hogy a
                        jelentkezettnek a kapacitás nem hír. Ez az edzőre NEM igaz: a hozzárendelt
                        edző is `UserEvents`-sor, ezért a szerver neki is `isRegistered: true`-t ad
                        (`backend/PinyoApi/Controllers/CalendarController.cs:103`), így az edző a
                        saját edzésein soha nem látta, hogy megtelt-e. Szerepkör-feltételt döntés
                        nélkül nem vezetünk be — a MELLÉ tétellel egyik szerepkörnél sem tűnik el
                        információ. A függő eseményen (isPending) a jelző továbbra sem jelenik meg
                        (`showRegisteredMarker`: függő > Jelentkeztél).
                      */}
                      {(showRegisteredMarker(event) || showCapacity) && (
                        <Box
                          data-testid="weekly-card-badges"
                          sx={{ mt: 0.25, display: 'flex', flexWrap: 'wrap', gap: '2px', alignItems: 'center' }}
                        >
                          {showRegisteredMarker(event) && <RegisteredMarker size="tiny" />}
                          {showCapacity && (
                            <Chip
                              label={weeklyCapacityLabel(event)}
                              size="small"
                              sx={{
                                height: '14px',
                                fontSize: '0.55rem',
                                backgroundColor: event.availableSpots > 0 ? 'success.main' : 'error.main',
                                color: 'white',
                                // [Web UX 1. csomag, 2026-10-03] belső margó 8→4 px MINDEN kártyán: így
                                // fér ki a „Jelentkeztél" jelző és a kapacitás-címke egy sorban a keskeny
                                // heti oszlopban (az 1. csomagban 320–1280 px-en mérve). Egyedül álló
                                // címkénél is ez marad, hogy a kártyák egységesek legyenek.
                                '& .MuiChip-label': { px: '4px' },
                              }}
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  </Tooltip>
                  );
                })
              )}
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Event Dialogs */}
      {selectedEventId && (
        <>
          <EventDetailsDialog
            eventId={selectedEventId}
            open={detailsDialogOpen}
            onClose={handleDialogClose}
            onEdit={handleEditClick}
            onNavigateToUserProfile={onNavigateToUserProfile}
            onNavigateToAttendance={onNavigateToAttendance}
          />
          <EventEditDialog
            eventId={selectedEventId}
            open={editDialogOpen}
            onClose={handleDialogClose}
          />
        </>
      )}

      {isAdminOrCoach && (
        <EventCreateDialog
          open={createDialogOpen}
          onClose={handleDialogClose}
          initialDate={createEventDate}
          initialStartTime={createEventTime}
        />
      )}
    </Box>
  );
};

export default WeeklyCalendar;
// Cache buster: 1762282059
